import { earnsContactHours, type CreditableKind } from "./continuing-education";

/**
 * How a teacher came to a session, which is not the same question as what the session was.
 *
 * The convention takes six seminars from every teacher who attends it: ten run at once and
 * she can sit in one. Decision 10 approved repairing that — "a teacher forced to choose
 * between two sessions in one slot gets the other one afterwards" — and Decision 97 settled
 * how she gets it: she asks, one session at a time.
 *
 * What she gets is the session's material. What she must never get is the session's hours.
 *
 * The AACS voucher is an attendance document. She lists the sessions she attended, signs it,
 * an administrator signs to verify she was there, and it goes with her certification renewal.
 * Decision 10 also settled that any recording of a session earns its full stated hours with no
 * duration test — a deliberate kindness to the teacher whose phone failed. Put those two
 * together with a record built from somebody else's recording and a session nobody attended
 * reaches a form two people sign.
 *
 * So the distinction is carried here, in the data, rather than left to a teacher to remember
 * while she ticks boxes. `attended` is the ordinary state and needs no ceremony. `studied` is
 * the one the software has to keep saying out loud.
 */

export const SESSION_STANDINGS = ["attended", "studied"] as const;

export type SessionStanding = (typeof SESSION_STANDINGS)[number];

/**
 * Whether this session may go on the voucher at all.
 *
 * Two questions, and both must answer yes. Was it the kind of thing the form counts — which
 * is seminars and nothing else — and was she there.
 *
 * Written as one function rather than two checks at the call site, because a rule enforced by
 * remembering to check it is a rule that survives exactly as long as the person who wrote it.
 */
export function mayBeListedForCredit(kind: CreditableKind, standing: SessionStanding): boolean {
  if (standing === "studied") return false;
  return earnsContactHours(kind);
}

/**
 * What a studied session is called wherever it appears. Not to be reworded on a screen.
 *
 * "Studied" rather than "attended", and never a word that could be read as either. The whole
 * value of the distinction is that it is visible on the face of the record.
 */
export const STUDIED_LABEL = "Studied afterwards";

/**
 * Said where a teacher might otherwise wonder why a session is not on her form.
 *
 * It explains rather than apologizes. She did a good thing — she went and read the material
 * for a seminar she could not attend — and the reason it earns nothing is the form's, not a
 * judgement about her.
 */
export const STUDIED_EXPLANATION =
  "You studied this one afterwards, so it is yours to keep but not to list. The form asks for sessions you attended.";

/**
 * Her standing in a session, from what the software already knows.
 *
 * Saving is the nearest thing this product has to an attendance list, and it is deliberately
 * the same rule that decides whether the session's transcript is hers to read: she put it on
 * her schedule before it ended. Anything else is study — including a session she added to her
 * schedule the following week, which is asking for material rather than remembering a room
 * she sat in.
 */
export function standingFrom(input: {
  savedBeforeItEnded: boolean;
  requestedAfterwards: boolean;
}): SessionStanding | null {
  if (input.savedBeforeItEnded) return "attended";
  if (input.requestedAfterwards) return "studied";
  // Neither. She has no standing in this session at all, which is a different answer from
  // having the wrong kind of standing, and the caller has to be able to tell them apart.
  return null;
}
